import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { ArrowRight, BadgeCheck, Check, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VideoOverlay } from "./VideoOverlay";
import heroDashboard from "@/assets/hero-dashboard.png";

export function HeroSection() {
  const { t, i18n } = useTranslation();
  const [videoOpen, setVideoOpen] = useState(false);

  const videoUrl = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/public-assets/videos/${i18n.language === "de" ? "CheckMaty_App_Vorstellung_DE.mp4" : "CheckMaty_App_Introduction_EN.mp4"}`;

  // TES-1072 Teil C: belastbare Eigenschafts-Claims statt erfundener Kennzahlen.
  const claims = [
    t("landing.industrial.hero.claims.gdpr"),
    t("landing.industrial.hero.claims.hosting"),
    t("landing.industrial.hero.claims.sso"),
  ];

  return (
    <section className="relative flex min-h-[calc(100vh-4rem)] items-center overflow-hidden bg-brand text-white">
      {/* Deep-blue industrial backdrop: technical grid */}
      <div
        className="absolute inset-0 z-0 opacity-[0.12]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
        aria-hidden="true"
      />

      {/* Product dashboard mockup — restored hero background image (pre-D5 hero).
          Desktop: contain, anchored center-right behind the copy. */}
      <div
        className="absolute inset-0 z-0 hidden md:block"
        style={{
          backgroundImage: `url(${heroDashboard})`,
          backgroundSize: "contain",
          backgroundPosition: "center right",
          backgroundRepeat: "no-repeat",
        }}
        aria-hidden="true"
        data-testid="hero-bg-image"
      />
      {/* Mobile: faded, scaled mockup so the headline stays readable on small screens. */}
      <div
        className="absolute inset-0 z-0 md:hidden"
        style={{
          backgroundImage: `url(${heroDashboard})`,
          backgroundSize: "200%",
          backgroundPosition: "center 60%",
          backgroundRepeat: "no-repeat",
          opacity: 0.12,
        }}
        aria-hidden="true"
      />

      {/* Deep-blue readability gradient: opaque left → translucent right so the
          mockup shows through on the right while the copy stays legible on the left. */}
      <div
        className="absolute inset-0 z-0"
        style={{
          background:
            "linear-gradient(100deg, hsl(var(--brand)) 0%, hsl(var(--brand) / 0.94) 40%, hsl(var(--brand) / 0.55) 70%, hsl(var(--brand-container) / 0.30) 100%)",
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        {/* Copy + CTAs + stats (single column; mockup is the hero backdrop) */}
        <div className="max-w-xl space-y-8 lg:max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-lg border border-secondary-fixed/30 bg-secondary/10 px-3 py-1 text-secondary-fixed">
            <BadgeCheck className="h-4 w-4" />
            <span className="text-[11px] font-bold uppercase tracking-wider">
              {t("landing.industrial.badge")}
            </span>
          </div>

          {/* sm:leading-none (TES-1062): Tailwind 3 liess sm:text-5xl/lg:text-6xl (line-height 1)
              per Media-Query-Reihenfolge ueber leading-tight gewinnen; Tailwind 4 laesst leading-*
              immer gewinnen (--tw-leading). Explizit gepinnt, damit der Hero pixel-identisch bleibt. */}
          <h1 className="max-w-xl font-display text-4xl font-bold leading-tight tracking-display text-white sm:text-5xl sm:leading-none lg:text-6xl">
            {t("landing.industrial.hero.titleLead")}{" "}
            <span className="text-secondary-fixed">{t("landing.industrial.hero.titleAccent")}</span>
          </h1>

          {/* sm:leading-7 (TES-1062): analog — v3-effektive line-height von sm:text-lg (1.75rem) gepinnt. */}
          <p className="max-w-lg text-base leading-relaxed text-white/80 sm:text-lg sm:leading-7">
            {t("landing.industrial.hero.subtitle")}
          </p>

          <div className="flex flex-wrap gap-4 pt-2">
            <Button variant="action" size="lg" className="group px-8 py-6 text-base" asChild>
              <Link to="/auth" data-testid="hero-launch-cta">
                {t("landing.industrial.hero.launch")}
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/30 bg-transparent px-8 py-6 text-base text-white hover:bg-white/10 hover:text-white"
              onClick={() => setVideoOpen(true)}
            >
              <PlayCircle className="mr-2 h-5 w-5" />
              {t("landing.hero.demo")}
            </Button>
          </div>

          {/* Hero property badges (truthful claims, TES-1072 Teil C) */}
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-white/10 pt-8">
            {claims.map((claim) => (
              <li key={claim} className="flex items-center gap-2">
                <Check className="h-4 w-4 shrink-0 text-secondary-fixed" aria-hidden="true" />
                <span className="text-sm font-semibold text-white">{claim}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <VideoOverlay open={videoOpen} onOpenChange={setVideoOpen} videoUrl={videoUrl} />
    </section>
  );
}
